import React, { useState } from 'react';
import { Loader2, ImageIcon, Check } from 'lucide-react';
import { Modal, Button } from '../ui';

interface ImagePickerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  images: string[];
  onSave: (url: string) => Promise<void>;
  isSaving: boolean;
}

export const ImagePickerDialog: React.FC<ImagePickerDialogProps> = ({
  isOpen,
  onClose,
  images,
  onSave,
  isSaving,
}) => {
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);
  const [failedUrls, setFailedUrls] = useState<Set<string>>(new Set());

  const handleSave = async () => {
    if (!selectedUrl) return;
    await onSave(selectedUrl);
    setSelectedUrl(null);
  };

  const handleClose = () => {
    setSelectedUrl(null);
    onClose();
  };

  const markFailed = (url: string) => {
    setFailedUrls((prev) => new Set(prev).add(url));
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Select Product Image" size="xl">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted">
          {images.length} image{images.length !== 1 ? 's' : ''} found. Click one to select it, then click Save.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto pr-1">
          {images.map((url) => {
            const isSelected = selectedUrl === url;
            const hasFailed = failedUrls.has(url);

            return (
              <button
                key={url}
                type="button"
                onClick={() => setSelectedUrl(url)}
                className={`relative aspect-square rounded-lg border-2 overflow-hidden bg-surface-2 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                  isSelected
                    ? 'border-accent shadow-md'
                    : 'border-line hover:border-line-strong'
                }`}
              >
                {hasFailed ? (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-muted">
                    <ImageIcon size={24} />
                    <span className="text-xs">Failed</span>
                  </div>
                ) : (
                  <img
                    src={url}
                    alt="Product image"
                    className="w-full h-full object-contain p-1"
                    onError={() => markFailed(url)}
                  />
                )}
                {isSelected && (
                  <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-accent-soft0 flex items-center justify-center">
                    <Check size={12} className="text-accent-fg" />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex justify-end gap-3 pt-2 border-t border-line">
          <Button variant="secondary" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!selectedUrl || isSaving}>
            {isSaving ? (
              <><Loader2 size={16} className="animate-spin" /> Saving...</>
            ) : (
              'Save'
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
